import { useMemo, useState } from "react";
import { Separator } from "@/components/ui/separator";
import Header from "./components/header";
import { useTranslation } from "react-i18next";
import { useGetTransactionsByDateRange } from "@/services/queries/transactions";
import { useSearchParams } from "react-router-dom";
import useVessel from "@/store/use-vessel";
import {
  getFirstDayOfMonth,
  getLastDayOfMonth,
  formatDate,
  parseDateParam,
} from "@/lib/utils";
import { subDays, addDays } from "date-fns";
import Transactions from "./components/transactions";
import Tasks from "./components/tasks";
import { useGetTasks } from "@/services/queries/maintenance";
import useAuth from "@/store/use-auth";
import { useHasPermission } from "@/hooks/use-permission";
import { PERMISSIONS } from "@/lib/constants/permissions";

const DATE_RANGE = 7;

const Home = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const date = searchParams.get("date");
  const [isDateSelected, setIsDateSelected] = useState(false);
  const { selectedVessel } = useVessel();
  const { user } = useAuth();
  const canManageMaintenance = useHasPermission(
    PERMISSIONS.Maintenance.Management.Write
  );
  const {
    data: tasksData,
    isPending: isTasksPending,
    isRefetching: isTasksRefetching,
    refetch: refetchTasks,
  } = useGetTasks({
    vesselId: selectedVessel?.id,
    pageSize: 1000000, // To get all tasks
  });

  const tasks = useMemo(() => {
    return tasksData?.items || [];
  }, [tasksData]);

  const currentDate = useMemo(() => parseDateParam(date), [date]);
  const firstDayOfMonth = useMemo(
    () => getFirstDayOfMonth(currentDate),
    [currentDate]
  );
  const lastDayOfMonth = useMemo(
    () => getLastDayOfMonth(currentDate),
    [currentDate]
  );
  const fromDate = useMemo(
    () => subDays(firstDayOfMonth, DATE_RANGE),
    [firstDayOfMonth]
  );
  const toDate = useMemo(
    () => addDays(lastDayOfMonth, DATE_RANGE),
    [lastDayOfMonth]
  );

  const { data, isPending, isRefetching, refetch } =
    useGetTransactionsByDateRange({
      fromDate,
      toDate,
      vesselId: selectedVessel?.id,
    });

  const transactions = useMemo(() => {
    return data?.value || [];
  }, [data]);

  const filteredTransactions = useMemo(() => {
    const currentMonth = currentDate.getMonth();
    const formattedDate = formatDate(currentDate, "yyyy-MM-dd");

    return transactions.filter((transaction) => {
      const transactionDate = new Date(transaction.transactionDate);
      const isSameMonth = transactionDate.getMonth() === currentMonth;

      return isDateSelected
        ? formatDate(transaction.transactionDate, "yyyy-MM-dd") ===
            formattedDate
        : isSameMonth;
    });
  }, [isDateSelected, transactions, currentDate]);

  const tasksByUser = useMemo(() => {
    if (canManageMaintenance) {
      return tasks;
    }
    return tasks.filter((task) =>
      task.taskDetail.assignees?.some((a) => a.id === user?.sub)
    );
  }, [tasks, user, canManageMaintenance]);

  const tasksByFilteredByDate = useMemo(() => {
    if (!isDateSelected) {
      return tasksByUser;
    }

    return tasksByUser.filter((task) => {
      if (task.taskDetail.nextDate) {
        return (
          formatDate(task.taskDetail.nextDate, "yyyy-MM-dd") ===
          formatDate(currentDate, "yyyy-MM-dd")
        );
      }
      if (task.taskDetail.dueDate) {
        return (
          formatDate(task.taskDetail.dueDate, "yyyy-MM-dd") ===
          formatDate(currentDate, "yyyy-MM-dd")
        );
      }
      return false;
    });
  }, [tasksByUser, currentDate, isDateSelected]);

  const handleRefetch = () => {
    refetch();
  };

  return (
    <>
      <title>{t("title.monthly")}</title>
      <Header
        transactions={transactions}
        isDateSelected={isDateSelected}
        setIsDateSelected={setIsDateSelected}
        tasks={tasksByUser}
      />
      <Separator className="my-5" />
      <Transactions
        transactions={filteredTransactions}
        isPending={isPending || isRefetching}
        refetch={handleRefetch}
      />
      <Tasks
        tasks={tasksByFilteredByDate}
        isPending={isTasksPending || isTasksRefetching}
        refetchTasks={refetchTasks}
      />
    </>
  );
};

export default Home;
